import { forwardRef, Fragment } from "react";
import {
  IconArrowsMaximize,
  IconChevronDown,
  IconChevronsDown,
  IconChevronsUp,
  IconChevronUp,
  IconCopyPlus,
  IconDots,
  IconEye,
  IconEyeOff,
  IconLayoutAlignBottom,
  IconLayoutAlignCenter,
  IconLayoutAlignLeft,
  IconLayoutAlignMiddle,
  IconLayoutAlignRight,
  IconLayoutAlignTop,
  IconStack2,
  IconTrash,
} from "@tabler/icons-react";
import { getAllElements } from "@repo/api/utils/composition";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import {
  ALIGN_SHORTCUT_KEYS,
  AlignType,
} from "@/lib/stores/canvas-store/canvas-align";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { useCanvasStore } from "@/lib/stores/canvas-store/canvas-store";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import type { ElementOrdering } from "@/lib/stores/canvas-store/canvas-layer-order";
import { Box, getBoxStyle, Resolution } from "./canvas-geometry";
import { getShortcutLabel, IS_MAC, ORDERING_SHORTCUTS } from "./shortcuts";

const SHORTCUT_LABELS = {
  duplicate: getShortcutLabel({ code: "KeyD", label: "D", mod: true }),
  delete: IS_MAC ? "⌫" : "Delete",
  visibility: getShortcutLabel({ code: "KeyH", label: "H", mod: true }),
};

type AlignItem = {
  alignType: AlignType;
  icon: React.ElementType;
  label: string;
};

const ALIGN_GROUPS: AlignItem[][] = [
  [
    { alignType: "left", icon: IconLayoutAlignLeft, label: "Left" },
    { alignType: "center", icon: IconLayoutAlignCenter, label: "Center" },
    { alignType: "right", icon: IconLayoutAlignRight, label: "Right" },
  ],
  [
    { alignType: "top", icon: IconLayoutAlignTop, label: "Top" },
    { alignType: "middle", icon: IconLayoutAlignMiddle, label: "Middle" },
    { alignType: "bottom", icon: IconLayoutAlignBottom, label: "Bottom" },
  ],
];

const ORDERING_ITEMS: {
  ordering: ElementOrdering;
  icon: React.ElementType;
  label: string;
}[] = [
  { ordering: "bring-to-front", icon: IconChevronsUp, label: "Bring to front" },
  { ordering: "bring-forward", icon: IconChevronUp, label: "Bring forward" },
  { ordering: "send-backward", icon: IconChevronDown, label: "Send backward" },
  { ordering: "send-to-back", icon: IconChevronsDown, label: "Send to back" },
];

type Props = {
  box: Box;
  resolution: Resolution;
};

export default function ActionBar({ box, resolution }: Props) {
  const canvasService = useCanvasStore((state) => state.service);
  const isAllVisible = useEditorStore((state) =>
    getAllElements(state.video?.compositionJson)
      .filter(({ element }) =>
        state.selectedElementIds.includes(element.externalId),
      )
      .every(({ element }) => !element.isHidden),
  );

  const canMakeFullscreen = useEditorStore(
    (state) =>
      state.selectedElementIds.length === 1 &&
      getAllElements(state.video?.compositionJson).some(
        ({ element }) =>
          element.externalId === state.selectedElementIds[0] &&
          "size" in element,
      ),
  );

  const visibilityIcon = isAllVisible ? <IconEye /> : <IconEyeOff />;
  const visibilityLabel = isAllVisible ? "Hide" : "Show";

  return (
    <div className="absolute" style={getBoxStyle(box, resolution)}>
      <div
        data-keep-selection
        className="pointer-events-auto absolute bottom-[calc(100%+12px)] left-1/2 flex -translate-x-1/2 overflow-hidden rounded-[7px] border border-border bg-white text-text-primary"
      >
        {canMakeFullscreen && (
          <ActionButton
            icon={<IconArrowsMaximize size={18} />}
            label="Make fullscreen"
            onClick={canvasService.makeSelectionFullscreen}
          />
        )}
        <ActionButton
          icon={<IconCopyPlus size={18} />}
          label="Duplicate"
          shortcut={SHORTCUT_LABELS.duplicate}
          onClick={canvasService.duplicateSelection}
        />
        <ActionButton
          icon={<IconTrash size={18} />}
          label="Delete"
          shortcut={SHORTCUT_LABELS.delete}
          onClick={canvasService.deleteSelection}
        />
        <ActionButton
          icon={isAllVisible ? <IconEye size={18} /> : <IconEyeOff size={18} />}
          label={visibilityLabel}
          shortcut={SHORTCUT_LABELS.visibility}
          onClick={canvasService.toggleSelectionVisibility}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <ActionButton icon={<IconDots size={18} />} label="More actions" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            data-keep-selection
            className="w-52"
            side="bottom"
            align="start"
          >
            {canMakeFullscreen && (
              <DropdownMenuItem onClick={canvasService.makeSelectionFullscreen}>
                <IconArrowsMaximize />
                <span>Make fullscreen</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={canvasService.duplicateSelection}>
              <IconCopyPlus />
              <span>Duplicate</span>
              <DropdownMenuShortcut>
                {SHORTCUT_LABELS.duplicate}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <IconLayoutAlignLeft />
                <span>Align</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent data-keep-selection className="w-44">
                {ALIGN_GROUPS.map((items, groupIndex) => (
                  <Fragment key={groupIndex}>
                    {groupIndex > 0 && <DropdownMenuSeparator />}
                    {items.map(({ alignType, icon: Icon, label }) => (
                      <DropdownMenuItem
                        key={alignType}
                        onClick={() => canvasService.alignSelection(alignType)}
                      >
                        <Icon />
                        <span>{label}</span>
                        <DropdownMenuShortcut>
                          {getShortcutLabel({
                            code: `Key${ALIGN_SHORTCUT_KEYS[alignType]}`,
                            label: ALIGN_SHORTCUT_KEYS[alignType],
                            alt: true,
                          })}
                        </DropdownMenuShortcut>
                      </DropdownMenuItem>
                    ))}
                  </Fragment>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <IconStack2 />
                <span>Layer</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent data-keep-selection className="w-44">
                {ORDERING_ITEMS.map(({ ordering, icon: Icon, label }) => (
                  <DropdownMenuItem
                    key={ordering}
                    onClick={() => canvasService.reorderSelection(ordering)}
                  >
                    <Icon />
                    <span>{label}</span>
                    <DropdownMenuShortcut>
                      {getShortcutLabel(ORDERING_SHORTCUTS[ordering])}
                    </DropdownMenuShortcut>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={canvasService.deleteSelection}>
              <IconTrash />
              <span>Delete</span>
              <DropdownMenuShortcut>
                {SHORTCUT_LABELS.delete}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={canvasService.toggleSelectionVisibility}>
              {visibilityIcon}
              <span>{visibilityLabel}</span>
              <DropdownMenuShortcut>
                {SHORTCUT_LABELS.visibility}
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

type ActionButtonProps = React.ComponentProps<"button"> & {
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
};

const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  ({ icon, label, shortcut, ...props }, ref) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          ref={ref}
          className="flex size-8 cursor-pointer items-center justify-center bg-white hover:bg-button-outline-hover focus-visible:outline-none"
          {...props}
        >
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        {label}
        {shortcut && <span className="ml-2 opacity-60">{shortcut}</span>}
      </TooltipContent>
    </Tooltip>
  ),
);
